import { useState, useEffect, useRef } from 'react';
import { eventBus } from '@/infrastructure/eventBus';
import { publishStorageChange } from '@/infrastructure/secureStorageEventBridge';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * Secure Storage Service using Web Crypto API (Native Browser Encryption)
 * Similar to Hive with encryption on Flutter
 * 
 * Uses IndexedDB for persistent storage with AES-GCM encryption via Web Crypto API
 * for sensitive user data that needs to be synced with server
 * 
 * ADVANTAGES over crypto-js:
 * - 10-100x faster (native implementation)
 * - More secure (hardware-accelerated, isolated context)
 * - 0 KB bundle size (built into browser)
 * - AES-GCM provides authenticated encryption (prevents tampering)
 */
export class SecureStorageWebCrypto {
  private static instance: SecureStorageWebCrypto;
  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'expause_secure_storage';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'secure_data';

  // Encryption key (CryptoKey object, not string)
  private encryptionKey: CryptoKey | null = null;

  private constructor() { }

  static getInstance(): SecureStorageWebCrypto {
    if (!SecureStorageWebCrypto.instance) {
      SecureStorageWebCrypto.instance = new SecureStorageWebCrypto();
    }
    return SecureStorageWebCrypto.instance;
  }

  /**
   * Derive encryption key from user ID using PBKDF2
   * This is more secure than simple string concatenation
   */
  private async deriveKey(userId: string, salt: string): Promise<CryptoKey> {
    // Convert userId to key material
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(userId + salt),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    // Derive AES-GCM key
    return await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(salt),
        iterations: 100000, // OWASP recommendation
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false, // Not extractable (more secure)
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Whether the storage is ready for read/write operations.
   * Returns false before `initialize()` has completed, or after `dispose()`.
   */
  isInitialized(): boolean {
    return this.db !== null && this.encryptionKey !== null;
  }

  /**
   * Initialize storage with encryption key
   * Call this after user authentication
   */
  async initialize(userId: string): Promise<void> {
    // Derive strong encryption key from userId
    const salt = process.env.REACT_APP_ENCRYPTION_SALT || 'default-salt-change-in-production';
    this.encryptionKey = await this.deriveKey(userId, salt);

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME);
        }
      };
    });
  }

  /**
   * Encrypt data using AES-GCM
   * AES-GCM provides both confidentiality and authenticity (prevents tampering)
   */
  private async encrypt(data: string): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> {
    if (!this.encryptionKey) throw new Error('Storage not initialized');

    // Generate random IV (Initialization Vector) for each encryption
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    const encoder = new TextEncoder();
    const ciphertext = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      this.encryptionKey,
      encoder.encode(data)
    );

    return { ciphertext, iv };
  }

  /**
   * Decrypt data using AES-GCM
   */
  private async decrypt(ciphertext: ArrayBuffer, iv: number[]): Promise<string> {
    if (!this.encryptionKey) throw new Error('Storage not initialized');

    const decrypted = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(iv),
      },
      this.encryptionKey,
      ciphertext
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  }

  /**
   * Store encrypted data
   * @param key Storage key (e.g., 'liked_videos', 'premium_packages')
   * @param value Data to store
   */
  async setEncrypted<T>(key: string, value: T): Promise<void> {
    if (!this.db) throw new Error('Storage not initialized');

    const jsonData = JSON.stringify(value);
    const { ciphertext, iv } = await this.encrypt(jsonData);

    // Store both ciphertext and IV (IV doesn't need to be secret)
    const encryptedData = {
      ciphertext: Array.from(new Uint8Array(ciphertext)), // Convert to array for storage
      iv: Array.from(iv),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.put(encryptedData, key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        eventBus.emit(key, { value });
        // Mirror the write to other tabs. Same-tab listeners are already
        // notified via the eventBus.emit above; the bridge only re-emits
        // on the receiving side. See `secureStorageEventBridge.ts`.
        publishStorageChange(key, value);
        resolve();
      };
    });
  }

  /**
   * Retrieve and decrypt data
   */
  async getEncrypted<T>(key: string): Promise<T | null> {
    if (!this.db) throw new Error('Storage not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = async () => {
        const encryptedData = request.result;
        if (!encryptedData) {
          resolve(null);
          return;
        }

        try {
          // Convert arrays back to typed arrays
          const ciphertext = new Uint8Array(encryptedData.ciphertext).buffer;
          const iv = encryptedData.iv; // Keep as number array

          const decryptedStr = await this.decrypt(ciphertext, iv);
          resolve(JSON.parse(decryptedStr));
        } catch (error) {
          appLogger.error('Decryption failed', 'SecureStorageWebCrypto.getEncrypted', { error: error as Error });
          resolve(null);
        }
      };
    });
  }

  /**
   * Remove data
   */
  async remove(key: string): Promise<void> {
    if (!this.db) throw new Error('Storage not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        eventBus.emit(key, { value: null });
        // Mirror the delete to other tabs — see `setEncrypted` above.
        publishStorageChange(key, null);
        resolve();
      };
    });
  }

  /**
   * Get all keys that start with a specific prefix
   * Useful for finding all items of a certain type (e.g., all user settings)
   * 
   * Uses IDBKeyRange for efficient prefix queries - no full database scan!
   * Performance: O(log n + k) where k is number of matching keys
   */
  async getAllKeysWithPrefix(prefix: string): Promise<string[]> {
    if (!this.db) throw new Error('Storage not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);

      // Use key range for efficient prefix query (leverages B-tree index)
      // \uffff is the highest Unicode character, so this matches all keys starting with prefix
      const range = IDBKeyRange.bound(
        prefix,
        prefix + '\uffff',
        false,  // lowerOpen: false (include prefix)
        false   // upperOpen: false (include prefix + \uffff)
      );

      const request = store.getAllKeys(range);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        resolve(request.result as string[]);
      };
    });
  }

  /**
   * Get all encrypted values that start with a specific prefix
   * More efficient than getAllKeysWithPrefix + multiple getEncrypted calls
   * 
   * Returns array of decrypted values matching the prefix
   * Performance: Single DB query with range, O(log n + k) where k is number of matches
   * 
   * Processes decryption in batches to avoid blocking the UI with too many parallel operations
   * 
   * @example
   * // Get all liked videos
   * const likes = await storage.getAllWithPrefix<UserMediaLikeStorage>(SECURE_STORAGE_KEYS.LIKES);
   */
  async getAllWithPrefix<T>(prefix: string, batchSize: number = 20): Promise<T[]> {
    if (!this.db) throw new Error('Storage not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);

      // Use key range for efficient prefix query
      const range = IDBKeyRange.bound(
        prefix,
        prefix + '\uffff',
        false,
        false
      );

      const request = store.getAll(range);

      request.onerror = () => reject(request.error);
      request.onsuccess = async () => {
        const encryptedItems = request.result;

        if (!encryptedItems || encryptedItems.length === 0) {
          resolve([]);
          return;
        }

        try {
          const allDecrypted: T[] = [];

          // Process in batches to avoid blocking UI
          for (let i = 0; i < encryptedItems.length; i += batchSize) {
            const batch = encryptedItems.slice(i, i + batchSize);

            const decryptPromises = batch.map(async (encryptedData: any): Promise<T | null> => {
              try {
                const ciphertext = new Uint8Array(encryptedData.ciphertext).buffer;
                const iv = encryptedData.iv;
                const decryptedStr = await this.decrypt(ciphertext, iv);
                return JSON.parse(decryptedStr) as T;
              } catch (error) {
                appLogger.error('Failed to decrypt item', 'SecureStorageWebCrypto.getAllWithPrefix', { error: error as Error });
                return null;
              }
            });

            const batchResults = await Promise.all(decryptPromises);
            const validBatchItems = batchResults.filter((item): item is NonNullable<typeof item> => item !== null);
            allDecrypted.push(...(validBatchItems as T[]));

            // Yield to UI between batches (allows React to re-render)
            if (i + batchSize < encryptedItems.length) {
              await new Promise(resolve => setTimeout(resolve, 0));
            }
          }

          resolve(allDecrypted);
        } catch (error) {
          appLogger.error('Failed to decrypt items', 'SecureStorageWebCrypto.getAllWithPrefix', { error: error as Error });
          resolve([]);
        }
      };
    });
  }

  /**
   * Clear all data (on logout)
   * Works even if storage is not initialized - will open DB, clear, and close
   */
  async clear(): Promise<void> {
    // If DB is already open, use it
    if (this.db) {
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.clear();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    }

    // If DB is not open, open it temporarily to clear
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        const db = request.result;

        try {
          const transaction = db.transaction([this.STORE_NAME], 'readwrite');
          const store = transaction.objectStore(this.STORE_NAME);
          const clearRequest = store.clear();

          clearRequest.onerror = () => {
            db.close();
            reject(clearRequest.error);
          };

          clearRequest.onsuccess = () => {
            db.close();
            resolve();
          };
        } catch (error) {
          db.close();
          reject(error);
        }
      };
    });
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }

    this.encryptionKey = null;
  }
}

/**
 * Hook for reactive storage updates (similar to Hive's watch)
 */
export function useListenToSecureStorageUpdates<T>(key: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storage = SecureStorageWebCrypto.getInstance();

    // Initial load — skipped if storage hasn't been initialized yet.
    // The eventBus subscription below will pick up the first write once it lands.
    if (storage.isInitialized()) {
      storage.getEncrypted<T>(key).then((value) => {
        setData(value);
        setLoading(false);
      }).catch((error) => {
        appLogger.error('Failed to load from secure storage', 'SecureStorageWebCrypto.useListenToSecureStorageUpdates', { error: error as Error });
        setLoading(false);
      });
    } else {
      setLoading(false);
    }

    // Listen for changes on this specific storage key
    const unsubscribe = eventBus.on(key, (data) => {
      setData(data.value as T | null);
    });

    return unsubscribe;
  }, [key]);

  const setValue = async (value: T) => {
    const storage = SecureStorageWebCrypto.getInstance();
    await storage.setEncrypted(key, value);
  };

  return { data, loading, setValue };
}

/**
 * Hook for reactive storage updates on all items sharing a key prefix.
 *
 * Always subscribes to per-key change events under `prefix`. Whether the
 * hook also pre-populates `items` from storage on mount is controlled by
 * `loadInitialItems` — see below.
 *
 * **Removals.** Per-item `remove(key)` emits `{ value: null }` and the
 * hook filters the entity out of `items` AND appends the key suffix to
 * `deletedKeys` so consumers that own their own collection (e.g. a hook
 * that already snapshotted the initial set through a domain service and
 * uses this hook purely as a live-update channel) can observe deletes
 * even for keys that were never in `items`. Bulk `clear()` does NOT emit
 * per-key events; callers that rely on staying in sync with a full wipe
 * need a different strategy.
 *
 * @param prefix            The shared storage-key prefix (e.g.
 *                          `'UnlockedContent_'`). Each stored entry's key is
 *                          `${prefix}${id}`; on a write event the hook
 *                          extracts the suffix `id` and locates the matching
 *                          in-memory entity to update / replace / remove.
 * @param getItemKey        Required. Returns the discriminator field on `T`
 *                          that equals the storage-key suffix for that
 *                          entry. Common shapes: `(item) => item.name`,
 *                          `(item) => item.path`, `(item) => item.id`.
 *                          Passing the wrong field silently breaks
 *                          update/remove deduplication, so every caller must
 *                          state this explicitly — there is no default.
 * @param loadInitialItems  Required. If `true`, the hook calls
 *                          `getAllWithPrefix` on mount and the returned
 *                          `items` array starts populated with everything
 *                          currently in storage (use this when the page
 *                          owns its data exclusively through the hook).
 *                          If `false`, `items` starts as `[]` and grows
 *                          only via subsequent live writes (use this when
 *                          the page already loads existing data through a
 *                          domain service and only needs the hook as a
 *                          live-update channel — avoids a redundant
 *                          `getAllWithPrefix` and, for large prefixes,
 *                          avoids returning the entire set into memory
 *                          when the caller never reads it).
 *
 * @returns                 `{ items, deletedKeys, loading }`. `deletedKeys`
 *                          accumulates the suffix (key minus prefix) of
 *                          every `remove`-style event observed since the
 *                          hook mounted. It is append-only by design:
 *                          consumers should treat it as a stream of
 *                          delete events and dedupe with their own
 *                          "already processed" set (typically the same
 *                          `seenIdsRef` they use for adds). Re-saves
 *                          after an unsave will appear as a new entry
 *                          in `items` and the consumer's `seenIdsRef`
 *                          handles the re-add path naturally.
 */
export function useListenToSecureStorageUpdatesByPrefix<T>(
  prefix: string,
  getItemKey: (item: T) => string,
  loadInitialItems: boolean,
) {
  const [items, setItems] = useState<T[]>([]);
  const [deletedKeys, setDeletedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(loadInitialItems);

  // Mirror `getItemKey` into a ref so the effect doesn't re-run when callers
  // pass an inline arrow (`(item) => item.path`) — that lambda gets a fresh
  // reference on every parent render, which would otherwise tear down and
  // re-register the eventBus listener (and, for `loadInitialItems: true`,
  // re-fetch IndexedDB) every render. Same hazard would apply to
  // `loadInitialItems` if it ever changed, but in practice it's always a
  // literal boolean at the call site — keep it out of the deps too.
  const getItemKeyRef = useRef(getItemKey);
  useEffect(() => {
    getItemKeyRef.current = getItemKey;
  });

  useEffect(() => {
    const storage = SecureStorageWebCrypto.getInstance();

    // Initial load — opt-in via `loadInitialItems`. Skipped if storage
    // hasn't been initialized yet; the eventBus subscription below will
    // still pick up the first write once it lands.
    if (loadInitialItems && storage.isInitialized()) {
      storage.getAllWithPrefix<T>(prefix).then((values) => {
        setItems(values);
        setLoading(false);
      }).catch((error) => {
        appLogger.error('Failed to load from secure storage by prefix', 'SecureStorageWebCrypto.useListenToSecureStorageUpdatesByPrefix', { error: error as Error });
        setLoading(false);
      });
    } else if (loadInitialItems) {
      setLoading(false);
    }

    // Listen for any change under this prefix — targeted update only
    const unsubscribe = eventBus.onPrefix(prefix, ({ key, value }) => {
      const itemName = key.slice(prefix.length);
      const updatedValue = value as T | null;

      if (updatedValue === null) {
        setItems((prev) => prev.filter((item) => getItemKeyRef.current(item) !== itemName));
        // Append-only delete stream — consumers dedupe via their own
        // `seenIds` set. See the JSDoc on this hook for the rationale.
        setDeletedKeys((prev) => [...prev, itemName]);
        return;
      }

      setItems((prev) => {
        const existingIndex = prev.findIndex((item) => getItemKeyRef.current(item) === itemName);
        if (existingIndex >= 0) {
          const next = [...prev];
          next[existingIndex] = updatedValue;
          return next;
        }
        return [...prev, updatedValue];
      });
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);

  return { items, deletedKeys, loading };
}
